// Pricing page — VARIANT B (A/B test). A shorter, marketing-heavy page that
// gives away far less of the actual report than the default /pricing. It
// matches the prior jobtest.org pricing design: emotional hero, opportunity
// cards, wealth potential, testimonials, coaching, expert coaches.
//
// Personalization is intentionally minimal to maximize the curiosity gap:
//   • greet by first name
//   • show the personalized earning-potential number
//   • show the CAPBOI radar (the one concrete "wow")
//   • everything else (top careers, strengths) is shown BLURRED behind a
//     click-to-unlock overlay (LockedReveal)
//
// Rendered by src/routes/Pricing.tsx when the visitor is assigned variant 'b'.
// Same prop signature as PricingContent so it's a drop-in.

import { RadarChart } from '../components/RadarChart';
import { PlanCard } from '../components/PlanCard';
import { FeaturedLogos } from '../components/FeaturedLogos';
import { PLANS, type PlanId } from '../data/plans';
import { REVIEWS } from '../data/reviews';
import { COACHES } from '../data/coaches';
import { firstNameOrNull } from '../lib/names';
import type { Report } from '../dashboard/reportTypes';

// ── helpers ──────────────────────────────────────────────────
function parseSalary(raw: string | undefined): number | null {
  if (!raw) return null;
  const n = parseInt(raw.replace(/[^0-9]/g, ''), 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}
function computeCeiling(topSenior: number | undefined, variables: Record<string, string>): number {
  if (topSenior && topSenior > 0) return topSenior;
  const cur = parseSalary(variables.currentSalary);
  if (cur) {
    if (cur < 50_000) return 100_000;
    if (cur < 100_000) return 150_000;
    if (cur < 150_000) return 220_000;
    if (cur < 250_000) return 320_000;
    return cur * 2;
  }
  return 198_000;
}
const fmtUsd = (n: number) => '$' + Math.round(n).toLocaleString('en-US');
const scrollToPricing = (e: React.MouseEvent) => {
  e.preventDefault();
  document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const UNIVERSITIES = [
  'UCLA', 'Johns Hopkins', 'Stanford', 'U. Chicago', 'Northwestern',
  'Harvard', 'MIT', 'Yale', 'Cornell', 'Princeton',
];

const OPPORTUNITY_CARDS = [
  { title: 'Custom Career Paths', caption: 'Explore options that align with your unique aspirations.', img: '/hero/p1.avif' },
  { title: 'Personalized Insights', caption: 'Gain understanding of your strengths and how to leverage them.', img: '/hero/p4.jpg' },
  { title: 'Empower Your Journey', caption: 'Take control with a clear path forward.', img: '/hero/p5.avif' },
];

// Mirrors the WeeklyCoaching design on Variant A (Pricing.tsx).
const COACHING_ITEMS: { title: string; body: string; iconBg: string; icon: React.ReactNode }[] = [
  {
    title: 'Weekly group coaching',
    body: 'Bring a problem, leave with a plan. Small-group calls every week.',
    iconBg: 'bg-brand-50 text-brand-700',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
  {
    title: 'Résumé & LinkedIn reviews',
    body: 'Get your résumé and LinkedIn rewritten live on the call.',
    iconBg: 'bg-teal-50 text-teal-700',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
        <line x1="9" y1="13" x2="15" y2="13" /><line x1="9" y1="17" x2="13" y2="17" />
      </svg>
    ),
  },
  {
    title: 'Weekly job roundup',
    body: 'Hand-picked openings each week that match your top careers.',
    iconBg: 'bg-ink/5 text-ink',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
      </svg>
    ),
  },
  {
    title: 'Offer & salary clinic',
    body: 'Bring your offer. Leave with the script to negotiate it up.',
    iconBg: 'bg-brand-50 text-brand-700',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
    ),
  },
  {
    title: 'Mock interview drills',
    body: 'Practice your top 5 answers with a coach and small peer group.',
    iconBg: 'bg-teal-50 text-teal-700',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" /><path d="M19 10v2a7 7 0 0 1-14 0v-2" />
        <line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" />
      </svg>
    ),
  },
  {
    title: 'Application sprint',
    body: 'Send 5 tailored applications in 90 minutes, together with peers.',
    iconBg: 'bg-ink/5 text-ink',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
      </svg>
    ),
  },
];

interface PricingContentBProps {
  report: Report | null;
  variables: Record<string, string>;
  answers: Record<string, { optionIds: string[]; freeText?: string; ts: number }>;
  leadName: string | null;
  quizStartedAt: number | null;
  quizCompletedAt: number | null;
  onSelect: (id: PlanId) => void;
  welcomeBack?: boolean;
}

export function PricingContentB({
  report,
  variables,
  leadName,
  onSelect,
  welcomeBack = false,
}: PricingContentBProps) {
  const firstName = firstNameOrNull(leadName);
  const ceiling = computeCeiling(report?.careers?.[0]?.salarySenior, variables);
  const current = parseSalary(variables.currentSalary);
  const pctIncrease =
    current && ceiling > current ? Math.round(((ceiling - current) / current) * 100) : null;

  return (
    <div className="min-h-dvh bg-canvas text-ink">
      <StickyUnlockBar />
      <main>
        <HeroB firstName={firstName} welcomeBack={welcomeBack} />

        <Band>
          <FeaturedLogos />
        </Band>

        <OpportunitiesB />
        <WealthB ceiling={ceiling} pctIncrease={pctIncrease} />
        <RadarB report={report} />
        <CoachingB />
        <ExpertsB />
        <MondaysB />
        <PlansB onSelect={onSelect} />
      </main>
      <FooterB />
    </div>
  );
}

// ── layout helpers ───────────────────────────────────────────
function Band({ children }: { children: React.ReactNode }) {
  return (
    <section className="border-y border-ink/10 bg-canvas-subtle/50 py-8">
      <div className="max-w-[1100px] mx-auto px-4 sm:px-8">{children}</div>
    </section>
  );
}
function Section({
  children,
  className = '',
  id,
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
}) {
  return (
    <section id={id} className={`max-w-[1100px] mx-auto px-4 sm:px-8 py-9 sm:py-12 ${className}`}>
      {children}
    </section>
  );
}
function CenterHead({ title, sub }: { title: React.ReactNode; sub?: string }) {
  return (
    <header className="text-center max-w-2xl mx-auto mb-10">
      <h2 className="text-display-md text-ink leading-tight mb-3 text-balance">{title}</h2>
      {sub && <p className="text-ink-soft text-base sm:text-lg text-pretty">{sub}</p>}
    </header>
  );
}
function PrimaryCta({ children }: { children: React.ReactNode }) {
  return (
    <a href="#pricing" onClick={scrollToPricing} className="btn-primary !w-full sm:!w-auto sm:!inline-flex">
      {children}
    </a>
  );
}

// ── sticky unlock bar ────────────────────────────────────────
function StickyUnlockBar() {
  return (
    <div className="sticky top-0 z-40 bg-ink text-white">
      <div className="max-w-[1100px] mx-auto px-4 sm:px-8 py-3 flex items-center justify-between gap-3">
        <p className="text-base sm:text-xl font-extrabold tracking-tight">
          Your Personalized Report is Ready
        </p>
        <a
          href="#pricing"
          onClick={scrollToPricing}
          className="flex-shrink-0 inline-flex items-center rounded-cta border-2 border-brand bg-brand text-ink font-extrabold text-sm sm:text-base px-4 py-1.5 sm:px-6 sm:py-2 hover:-translate-y-0.5 transition"
        >
          Unlock
        </a>
      </div>
    </div>
  );
}

// ── 1. hero ──────────────────────────────────────────────────
function HeroB({ firstName, welcomeBack }: { firstName: string | null; welcomeBack: boolean }) {
  return (
    <Section className="!py-10 sm:!py-16">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
        <div className="order-1">
          {welcomeBack && (
            <p className="inline-block rounded-full bg-teal-50 border-2 border-teal-600/40 text-teal-800 text-xs font-bold px-3 py-1 mb-4">
              {firstName ? `Welcome back, ${firstName} — your report's still here.` : "Welcome back — your report's still here."}
            </p>
          )}
          <h1 className="text-display-md sm:text-display-lg text-ink leading-[1.05] mb-4 text-balance">
            Goodbye Limitations.<br />Hello Possibilities.
          </h1>
          <p className="text-ink-soft text-lg sm:text-xl mb-7 max-w-lg">
            {firstName ? `${firstName}, ` : ''}20 minutes today will change your next 20 years.
          </p>
          <PrimaryCta>Unlock Your Report →</PrimaryCta>
          <div className="mt-5 flex flex-col gap-2">
            <p className="text-brand text-lg" aria-hidden>★ ★ ★ ★ ★</p>
            <p className="text-sm font-bold text-ink">100% money-back guarantee</p>
            <div className="flex items-center gap-3 mt-1">
              <AvatarStack />
              <p className="text-xs text-ink-soft">
                <span className="font-bold text-ink">850,000</span> people found direction — your turn is now.
              </p>
            </div>
          </div>
        </div>
        <div className="order-2">
          <div className="aspect-[4/3] rounded-3xl overflow-hidden border-2 border-ink/10 bg-canvas-subtle">
            <img src="/hero/p2.jpg" alt="" className="w-full h-full object-cover" loading="eager" />
          </div>
        </div>
      </div>
    </Section>
  );
}
function AvatarStack() {
  return (
    <div className="flex -space-x-2 flex-shrink-0">
      {COACHES.slice(0, 5).map((c) => (
        <img
          key={c.id}
          src={c.imageSrc}
          alt=""
          className="w-7 h-7 rounded-full border-2 border-canvas object-cover"
          loading="lazy"
          width={28}
          height={28}
        />
      ))}
    </div>
  );
}

// ── 2. opportunities ─────────────────────────────────────────
function OpportunitiesB() {
  return (
    <Section>
      <CenterHead title={<>Unlock new opportunities tailored to <em>you</em>.</>} />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
        {OPPORTUNITY_CARDS.map((c) => (
          <article key={c.title} className="relative rounded-3xl overflow-hidden border-2 border-ink/10 aspect-[3/4]">
            <img src={c.img} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/30 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-5">
              <h3 className="text-white text-lg font-extrabold mb-1">{c.title}</h3>
              <p className="text-white/85 text-sm leading-snug">{c.caption}</p>
            </div>
          </article>
        ))}
      </div>
    </Section>
  );
}

// ── 3. wealth ────────────────────────────────────────────────
function WealthB({ ceiling, pctIncrease }: { ceiling: number; pctIncrease: number | null }) {
  return (
    <Section>
      <CenterHead title="Find Your Path. Unlock Your Wealth." />
      <div className="rounded-3xl border-2 border-ink/10 bg-white overflow-hidden grid grid-cols-1 lg:grid-cols-2 items-center">
        <div className="p-8 sm:p-12">
          <p className="text-[44px] sm:text-[64px] font-extrabold leading-none tracking-tight mb-4">
            <span className="text-ink-light">$</span>
            <span className="text-ink tabular-nums">{Math.round(ceiling).toLocaleString('en-US')}</span>
          </p>
          <p className="text-xl sm:text-2xl font-extrabold text-ink mb-4 max-w-sm">
            We believe your earning potential can be {fmtUsd(ceiling)} and more.
          </p>
          <p className="border-l-4 border-brand pl-4 text-sm text-ink-soft leading-relaxed max-w-sm">
            {pctIncrease
              ? <>That's a <strong className="text-ink">{pctIncrease}% increase</strong> from your current salary, and you have the qualification and skillset to get there.</>
              : <>You have the qualification and skillset to get there — your report shows the exact roles and the path.</>}
          </p>
        </div>
        <div className="h-full min-h-[280px] bg-canvas-subtle">
          <img src="/hero/p6.jpg" alt="" className="w-full h-full object-cover" loading="lazy" />
        </div>
      </div>
    </Section>
  );
}

// ── 4. radar (visible) + blurred dominant teaser ─────────────
function RadarB({ report }: { report: Report | null }) {
  const p = report?.personality;
  if (!p?.radar || !p.peerRadar) {
    return null;
  }
  const dominant = Array.isArray(p.dominantAxes) ? p.dominantAxes : [];
  return (
    <Section>
      <CenterHead
        title="Your personality, decoded."
        sub="This is the one thing we'll show you up front. The rest of your report goes much deeper."
      />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
        <div className="rounded-3xl border-2 border-ink/10 bg-white p-6 sm:p-8 flex justify-center">
          <RadarChart
            user={p.radar}
            peer={p.peerRadar}
            highlight={dominant.map((a) => a.toLowerCase())}
            size={320}
          />
        </div>
        <div>
          {dominant.length > 0 && (
            <p className="text-lg text-ink mb-4">
              You scored above average on{' '}
              <span className="text-teal-700 font-bold">{dominant.join(' & ')}</span>.
            </p>
          )}
          <p className="text-ink-soft mb-5">
            Your full report breaks down all <strong className="text-ink">6 dimensions</strong>, your
            strengths, your blind spots, and the careers that fit each — but that part's locked.
          </p>
          <a href="#pricing" onClick={scrollToPricing} className="btn-primary !w-auto !inline-flex">
            Unlock my full breakdown →
          </a>
        </div>
      </div>
    </Section>
  );
}

// Trust stats box — sits above the testimonials.
function TrustBox() {
  return (
    <div className="rounded-3xl border-2 border-ink/10 bg-white px-5 py-5 sm:px-8 mb-8 flex flex-wrap items-center justify-center gap-x-8 gap-y-4 text-center">
      <div className="flex items-center gap-2">
        <span className="text-brand text-lg" aria-hidden>★ ★ ★ ★ ★</span>
        <span className="font-extrabold text-ink tabular-nums">4.9/5</span>
      </div>
      <span className="hidden sm:inline w-px h-6 bg-ink/15" />
      <div className="flex items-center gap-2.5">
        <AvatarStack />
        <span className="text-sm text-ink-soft">
          <strong className="text-ink tabular-nums">850,000</strong> people found direction
        </span>
      </div>
      <span className="hidden sm:inline w-px h-6 bg-ink/15" />
      <span className="text-sm font-bold text-ink">100% money-back guarantee</span>
    </div>
  );
}

// ── 5. testimonials ──────────────────────────────────────────
function MondaysB() {
  const picks = REVIEWS.slice(0, 6);
  const firstNameOf = (a: string) => a.trim().split(/[\s,]+/)[0] || a;
  return (
    <Section>
      <CenterHead
        title="Turn Mondays into Your Favorite Day."
        sub="Your dream job is not a myth. Join thousands of others who found their calling where you can't wait to start each week."
      />
      <TrustBox />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {picks.map((r) => (
          <article key={r.id} className="rounded-2xl border-2 border-ink/10 bg-white p-5">
            <div className="text-brand text-sm mb-2" aria-label={`${r.rating} stars`}>
              {'★'.repeat(r.rating)}
            </div>
            <h3 className="text-base font-extrabold text-ink mb-2">{r.title}</h3>
            <p className="text-sm text-ink-soft leading-relaxed mb-3">
              {r.body.length > 160 ? r.body.slice(0, 160).trimEnd() + '…' : r.body}
            </p>
            <p className="text-xs font-semibold text-ink">{firstNameOf(r.author)} · Verified</p>
          </article>
        ))}
      </div>
    </Section>
  );
}

// ── 6. coaching (A-style boxed treatment) ────────────────────
function CoachingB() {
  return (
    <Section>
      <div className="rounded-card border-2 border-ink/10 bg-canvas-tile p-5 sm:p-10 shadow-[6px_6px_0_0_rgba(15,18,23,1)]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-center mb-8 sm:mb-10">
          <div className="lg:col-span-7">
            <p className="eyebrow mb-3">Live coaching</p>
            <h2 className="text-display-md text-ink leading-tight mb-3 text-balance">
              Supercharge your career with live career coaching
            </h2>
            <p className="text-ink-soft text-base sm:text-lg max-w-xl">
              Real coaches, every week. Show up live, ask anything, and leave with one clear
              next step toward your next role.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="aspect-[4/3] rounded-3xl overflow-hidden border-2 border-ink/10 bg-canvas-subtle">
              <picture>
                <source srcSet="/coaches/hero.webp" type="image/webp" />
                <img
                  src="/coaches/hero.png"
                  alt="A career coach on a live video call"
                  className="w-full h-full object-cover"
                  loading="lazy"
                  width={1600}
                  height={711}
                />
              </picture>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {COACHING_ITEMS.map((c) => (
            <article key={c.title} className="rounded-2xl border-2 border-ink/10 bg-white p-5 flex items-start gap-3">
              <span className={`flex-shrink-0 w-10 h-10 rounded-xl ${c.iconBg} flex items-center justify-center`}>
                {c.icon}
              </span>
              <div className="min-w-0">
                <h3 className="text-base font-extrabold text-ink leading-tight mb-1">{c.title}</h3>
                <p className="text-sm text-ink-soft leading-relaxed">{c.body}</p>
              </div>
            </article>
          ))}
        </div>

        <p className="mt-6 text-sm text-ink-soft">
          <strong className="text-ink">99%</strong> received a job interview within 3 months.
        </p>
      </div>
    </Section>
  );
}

// ── 7. experts ───────────────────────────────────────────────
function ExpertsB() {
  return (
    <Section>
      <CenterHead
        title="Personalized support from experienced career experts."
        sub="Our 30+ specialists are dedicated to helping you achieve your career goals."
      />
      {/* University wordmarks (flagged: swap for real logo SVGs when available) */}
      <ul className="flex flex-wrap items-center justify-center gap-x-7 gap-y-3 mb-10">
        {UNIVERSITIES.map((u) => (
          <li key={u} className="text-ink/55 font-serif text-base sm:text-lg tracking-tight">
            {u}
          </li>
        ))}
      </ul>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
        {COACHES.map((c) => (
          <article key={c.id} className="rounded-2xl border-2 border-ink/10 bg-white overflow-hidden">
            <div className="aspect-square bg-canvas-subtle">
              <img src={c.imageSrc} alt={c.name} className="w-full h-full object-cover" loading="lazy" />
            </div>
            <div className="p-3">
              <h3 className="text-sm font-extrabold text-ink leading-tight">{c.name}</h3>
              <p className="text-xs text-ink-soft mb-1">{c.title}</p>
              {c.rating != null && (
                <p className="text-xs text-ink-soft">
                  <span className="text-brand">★</span>{' '}
                  <span className="tabular-nums text-ink font-semibold">{c.rating.toFixed(1)}</span>
                  {c.clientsServed && <span className="text-ink-light"> · {c.clientsServed} served</span>}
                </p>
              )}
            </div>
          </article>
        ))}
      </div>
    </Section>
  );
}

// ── 8. plans + blurred report peek ───────────────────────────
function PlansB({ onSelect }: { onSelect: (id: PlanId) => void }) {
  return (
    <Section id="pricing">
      {/* Header — mirrors the prior pricing design. */}
      <div className="text-center max-w-2xl mx-auto mb-8">
        <h2 className="text-display-md italic text-ink leading-tight mb-3 text-balance">
          Take a <strong className="font-extrabold">shortcut</strong> to the most fulfilling &amp;{' '}
          <strong className="font-extrabold">profitable</strong> years of your career
        </h2>
        <p className="text-ink-soft text-lg italic">Invest in yourself &amp; enjoy lifelong career success</p>
      </div>

      {/* 3 trust boxes — stay 3-wide on mobile (stacked/centered), horizontal on desktop. */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3 max-w-3xl mx-auto mb-9">
        <TrustItem iconBg="bg-teal-50 text-teal-600" title="Secure" sub="End-to-end encryption" icon={
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="m9 12 2 2 4-4" />
          </svg>
        } />
        <TrustItem iconBg="bg-brand-50 text-brand-700" title="Guarantee" sub="30-day money back" icon={
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <circle cx="12" cy="12" r="10" /><path d="M12 6v12M9.5 9a2.5 2.5 0 0 1 5 0c0 1.5-1 2-2.5 2.5S9.5 14 9.5 15a2.5 2.5 0 0 0 5 0" />
          </svg>
        } />
        <TrustItem iconBg="bg-blue-50 text-blue-600" title="24/7 Support" sub="Any issues resolved" icon={
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        } />
      </div>

      <p className="text-center text-lg sm:text-xl font-bold text-ink mb-7">
        Unlimited access with a flexible subscription — cancel anytime
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 items-start">
        {PLANS.map((plan) => (
          <PlanCard key={plan.id} plan={plan} selected={false} onSelect={() => onSelect(plan.id)} />
        ))}
      </div>

      <MoneyBackGuarantee />
    </Section>
  );
}

function TrustItem({
  icon,
  iconBg,
  title,
  sub,
}: {
  icon: React.ReactNode;
  iconBg: string;
  title: string;
  sub: string;
}) {
  return (
    <div className="rounded-2xl border-2 border-ink/10 bg-white p-3 sm:p-4 flex flex-col items-center text-center gap-2 sm:flex-row sm:text-left sm:gap-3">
      <span className={`flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-xl ${iconBg} flex items-center justify-center`}>
        {icon}
      </span>
      <div className="min-w-0">
        <p className="text-xs sm:text-sm font-bold text-ink leading-tight">{title}</p>
        <p className="text-[11px] sm:text-xs text-ink-soft leading-snug">{sub}</p>
      </div>
    </div>
  );
}

function MoneyBackGuarantee() {
  return (
    <div className="mt-12 sm:mt-16 text-center max-w-xl mx-auto">
      <div className="flex justify-center mb-4">
        <svg viewBox="0 0 140 140" className="w-32 h-32 sm:w-40 sm:h-40" role="img" aria-label="30-day money-back guarantee seal">
          <circle cx="70" cy="70" r="62" fill="#86e7d3" />
          <circle cx="70" cy="70" r="50" fill="none" stroke="#101828" strokeWidth="1.5" strokeDasharray="2 3" opacity="0.45" />
          <text x="70" y="48" textAnchor="middle" fontSize="9" fontWeight="800" fill="#101828" letterSpacing="2" fontFamily="Inter, sans-serif">MONEY BACK</text>
          <text x="70" y="82" textAnchor="middle" fontSize="30" fontWeight="900" fill="#101828" fontFamily="Inter, sans-serif">30</text>
          <text x="70" y="98" textAnchor="middle" fontSize="13" fontWeight="800" fill="#101828" fontFamily="Inter, sans-serif">DAY</text>
          <text x="70" y="116" textAnchor="middle" fontSize="8.5" fontWeight="800" fill="#101828" letterSpacing="1.5" fontFamily="Inter, sans-serif">GUARANTEE</text>
        </svg>
      </div>
      <h3 className="text-display-sm font-extrabold text-ink mb-3">100% Money-Back Guarantee</h3>
      <p className="text-ink-soft leading-relaxed">
        We believe that our report will help you find and achieve professional success and fulfillment.
        If our report doesn't deliver what it promises, we will refund your money. Please double check our{' '}
        <a href="/refund-policy" className="text-blue-600 underline hover:text-blue-700">Refund Policy</a> for more details.
      </p>
    </div>
  );
}

function FooterB() {
  return (
    <footer className="border-t border-ink/10 bg-canvas-subtle">
      <div className="max-w-[1100px] mx-auto px-4 sm:px-8 py-10 flex flex-wrap gap-4 items-center justify-between">
        <p className="text-sm font-extrabold text-ink">Jobtest.org</p>
        <div className="flex gap-5 text-sm font-semibold text-ink-soft">
          <a href="mailto:hello@jobtest.org" className="hover:text-ink transition">Contact</a>
          <a href="/login" className="hover:text-ink transition">Sign in</a>
        </div>
      </div>
    </footer>
  );
}
